/**
 * Cascade Forms API client — Supabase-backed, replacing the Airtable
 * Form_Entries source. The Worker forces client_id = cascade, so nothing here
 * passes a tenant.
 */

import type { FormSubmissionView } from '@command-center/oneup-portal-ui';
import type { FormTriageClassification, SharedRecordNote } from '@oneup/shared';
import { request, type PagedResponse } from './api';
import type { IntakeLifecycle } from './leadloop-api';

/** The three Form Entry views: the actionable queue, the Low Value tab, and All. */
export type FormTriageView = 'actionable' | 'low_value' | 'all';

export interface CascadeForm {
  id: string;
  name: string;
  externalFormId: string;
  siteUrl?: string;
  siteName?: string;
  pageUrl?: string;
  isActive: boolean;
  revision: number;
  fields: Array<{ label: string; type: string }>;
  schemaChangedAt?: string;
  submissionCount: number;
  lastSubmissionAt?: string;
  firstSeenAt: string;
}

/** The shared submission view type plus the Cascade-only promotion links. */
export interface CascadeSubmission extends FormSubmissionView {
  formId: string;
  airtableLeadRecordId?: string;
  airtableContactRecordId?: string;
  archived?: boolean;
  archivedAt?: string;
  notes?: SharedRecordNote[];
  /**
   * Where this entry stands in the intake lifecycle, derived server-side from
   * `intake_items` plus the record's notes. Optional: it postdates the DTO, so
   * an older cached response renders no badge rather than a wrong one.
   */
  lifecycle?: IntakeLifecycle;
  /** "Lead Created" / "Linked to Lead" / "Dismissed". Absent while open. */
  resolutionLabel?: string;
}

function withQuery(path: string, params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

export const formsApi = {
  forms: (params: { q?: string } = {}) =>
    request<{ items: CascadeForm[]; total: number }>(withQuery('/api/forms', { q: params.q })),

  form: (id: string) =>
    request<{ form: CascadeForm; submissions: CascadeSubmission[] }>(`/api/forms/${encodeURIComponent(id)}`),

  submissions: (params: { q?: string; reviewStatus?: string; triage?: FormTriageView; view?: string; formId?: string; dateFrom?: string; dateTo?: string; limit?: number; page?: number; pageSize?: number } = {}) =>
    request<PagedResponse<CascadeSubmission>>(withQuery('/api/form-submissions', {
      q: params.q,
      review_status: params.reviewStatus,
      triage: params.triage,
      form_id: params.formId,
      date_from: params.dateFrom,
      date_to: params.dateTo,
      limit: params.limit,
      view: params.view,
      page: params.page,
      pageSize: params.pageSize,
    })),

  submission: async (id: string) => {
    const response = await request<{ submission: CascadeSubmission; notes: SharedRecordNote[] }>(`/api/form-submissions/${encodeURIComponent(id)}`);
    return { ...response.submission, notes: response.notes };
  },
  setClassification: (id: string, classification: FormTriageClassification) =>
    request<{ submission: CascadeSubmission }>(
      `/api/form-submissions/${encodeURIComponent(id)}/classification`,
      { method: 'PATCH', body: JSON.stringify({ classification }) },
    ),
  addNote: (id: string, body: string) => request(`/api/form-submissions/${encodeURIComponent(id)}/notes`, {
    method: 'POST', body: JSON.stringify({ body }),
  }),
  archive: (id: string, archived: boolean) => request(`/api/form-submissions/${encodeURIComponent(id)}/${archived ? 'archive' : 'restore'}`, {
    method: 'POST',
  }),
};
